import { defineStore } from 'pinia';
import {
  readPersistedStoreState,
  writePersistedStoreState,
} from '@/stores/persistence/pinia-indexeddb';

const STORE_ID = 'shipmentComposerLearnedDedupSettings';
const DEFAULT_MATCH_PERCENT = 95;
const hydrationPromises = new WeakMap<object, Promise<void>>();

type PersistedLearnedDedupSettings = {
  dedupEnabled?: unknown;
  matchPercent?: unknown;
};

function clampMatchPercent(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_MATCH_PERCENT;
  return Math.min(100, Math.max(0, Math.round(numeric)));
}

function matchPercentFromThreshold(value: number | null): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  const percent = Math.min(100, Math.max(0, value * 100));
  return Math.round(percent * 10) / 10;
}

export const useShipmentFormLearnedDedupSettingsStore = defineStore(STORE_ID, {
  state: () => ({
    dedupEnabled: false,
    matchPercent: DEFAULT_MATCH_PERCENT,
    maxObservedMatchPercent: null as number | null,
    persistenceHydrated: false,
  }),
  getters: {
    matchThreshold(): number {
      return clampMatchPercent(this.matchPercent) / 100;
    },
  },
  actions: {
    hydrateFromBrowserDb(): Promise<void> {
      if (this.persistenceHydrated) return Promise.resolve();
      const pending = hydrationPromises.get(this);
      if (pending) return pending;

      const hydration = (async () => {
        try {
          const payload =
            await readPersistedStoreState<PersistedLearnedDedupSettings>(
              STORE_ID,
            );
          if (payload) {
            this.$patch({
              dedupEnabled:
                typeof payload.dedupEnabled === 'boolean'
                  ? payload.dedupEnabled
                  : false,
              matchPercent: clampMatchPercent(payload.matchPercent),
            });
          }
        } catch {
          // IndexedDB unavailable — keep safe production defaults.
        } finally {
          this.persistenceHydrated = true;
          hydrationPromises.delete(this);
        }
      })();
      hydrationPromises.set(this, hydration);

      return hydration;
    },
    persistToBrowserDb() {
      void writePersistedStoreState(STORE_ID, {
        dedupEnabled: this.dedupEnabled,
        matchPercent: clampMatchPercent(this.matchPercent),
      });
    },
    setDedupEnabled(value: boolean) {
      this.dedupEnabled = value;
      this.persistToBrowserDb();
    },
    setMatchPercent(value: number) {
      this.matchPercent = clampMatchPercent(value);
      this.persistToBrowserDb();
    },
    setMaxObservedMatch(value: number | null) {
      this.maxObservedMatchPercent = matchPercentFromThreshold(value);
    },
  },
});
